import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useDataChannel, useLocalParticipant, useRoomContext } from '@livekit/components-react'
import type { ReceivedDataMessage } from '@livekit/components-core'
import { RoomEvent } from 'livekit-client'

import { splitByBytes } from '../surface/chunk'
import { isTeacher } from '../useClassroomSignals'
import {
  fetchChatHistory,
  fetchUnfurl,
  moderateChat,
  persistChatMessages,
  putToS3,
  requestFileUpload,
  requestFileUrl,
  requestUpload,
  type ChatModerationAction,
  type Credential,
  type LinkPreview,
} from './chatApi'
import { chatFileRefusal, isSupportedChatFile, sanitizeFilename, sanitizeIncomingAttachment } from './attachment'
import { ImagePipelineError, processImage, type ImageError } from './image'
import {
  CHAT_QUEUE_CAP,
  CHAT_TOPIC,
  CLOCK_SLACK_MS,
  MAX_BODY_BYTES,
  RING,
  SEND_BURST,
  SEND_INTERVAL_MS,
  SNAPSHOT_CHUNK_BYTES,
  SNAPSHOT_MESSAGES,
  type ChatAttachment,
  type ChatMessage,
  type ChatModerationState,
  type ChatSnapshot,
  type ChatWire,
} from './types'

/**
 * The whole chat brain: ring buffer, ordering, dedup, rate limits, moderation
 * state, late-join snapshots, tutor rehydrate, and the async audit trail.
 *
 * ARCHITECTURE: this hook is an EXTERNAL STORE, not React state. It holds no
 * useState at all; consumers (ChatPanel, the unread badge) subscribe with
 * useSyncExternalStore. That is not a style choice — the hook mounts in
 * Classroom, and React state here re-rendered Classroom → Stage → every video
 * tile on every coalesced message commit. A 100-message flood became 6-10 full
 * classroom reconciles on a phone already decoding twenty streams, which is
 * precisely the stall the plan's SLO forbids. As a store, a flood re-renders
 * the chat panel and the badge, and nothing else. (Grok round-2, P1.)
 *
 * The other invariants this file enforces (chat plan v2 §1):
 * - Inbound packets COALESCE — many packets in one frame become one emit.
 * - The ring is hard-capped at 200 with `seenIds` evicted alongside.
 * - Ordering is (at, id) with receive-side clock clamp ±5min — reliable
 *   delivery is ordered per SENDER only, and student clocks are minutes wrong.
 * - Moderation gates read a SYNCHRONOUSLY updated stateRef. The previous
 *   render-time sync left a gap: a freeze and a student message in the same
 *   tick were gated against the pre-freeze state. (Grok round-2, S1.)
 * - The server (D1) is the only rev clock; DC events apply field-wise and only
 *   forward; tombstones can never resurrect — including rows ALREADY on screen
 *   when a durable snapshot carries the delete. (Grok round-2, S2.)
 */

interface OutboxEntry {
  id: string
  body: string
  at: number
  attachment?: ChatAttachment
}

/** The immutable snapshot consumers render from. A new object per change. */
export interface ChatUiSnapshot {
  messages: ChatMessage[]
  state: ChatModerationState
  unread: number
  refusal: SendRefusal
  moderationFailed: boolean
  frozen: boolean
  myChatMuted: boolean
  /**
   * Bumps only when a FOREIGN message is appended — the panel's "new messages"
   * chip keys off this, not off `messages` identity, which also changes for
   * upload-state upgrades and tombstones that are not news to the reader.
   */
  foreignSeq: number
  /**
   * Images still waiting to be decoded and sent, the active one included. The
   * composer greys its attach control off this, and the number is worth showing:
   * on a slow phone a three-photo drop takes long enough that silence reads as
   * "it didn't work" and invites the re-drop that actually does hurt.
   */
  pending: number
  /**
   * The file (non-image attachment, LK-009) currently queued or in flight —
   * icon + name + size for the composer chip, so a 10 MB PDF uploading over a
   * Cairo link has a visible state beyond a silent spinner.
   */
  pendingFile: { name: string; bytes: number } | null
  /**
   * The last attachment refusal, held in the STORE rather than the panel because the
   * queue outlives the call that filled it: a drop of four photos returns
   * immediately and the third one fails half a minute later, with nothing left
   * on the stack to hand the reason to.
   */
  imageRefusal: ImageRefusal | null
}

const OUTBOX_FLUSH_MS = 4_000
const OUTBOX_FLUSH_COUNT = 10
const OUTBOX_CAP = 100
const SNAPSHOT_TIMEOUT_MS = 1_500
const REQUEST_COOLDOWN_MS = 3_000
const MAX_PENDING_SNAPSHOTS = 4

export type SendRefusal = 'frozen' | 'muted' | 'tooLong' | 'tooFast' | 'image' | null
export type ImageRefusal =
  | ImageError
  | 'fileTooLarge'
  | 'notFile'
  | 'upload'
  | 'busy'
  | 'frozen'
  | 'muted'
  | 'notAllowed'

/**
 * Processed payloads kept for a same-id retry after a failed upload.
 * Images keep their decoded pair; a file (LK-009) keeps the raw bytes —
 * there is no processing to redo, only the PUT.
 */
type RetryPayload =
  | { kind: 'image'; full: Blob; thumb: Blob; width: number; height: number; name: string }
  | { kind: 'file'; blob: Blob; filename: string; size: number; content_type: string }
/** More abandoned failures than this and the oldest blobs are dropped. */
const RETRY_BLOBS_CAP = 3
/**
 * Cap on the resurrection-guard set. Must stay ABOVE the server's tombstone
 * limit (Meetings::ChatHistory returns up to 500 deleted ids) so the initial
 * durable seed never evicts a delete the server still knows about. An evicted
 * tombstone whose ring row has also gone (taking its seenIds entry with it) is
 * the one gap through which a deleted message can be re-displayed until the
 * next reconnect re-syncs. 600 leaves headroom over the 500 cap.
 */
const TOMBSTONE_CAP = 600

// sanitizeIncomingAttachment lives in attachment.ts (pure policy, tested).

/**
 * UTF-8 budget check without allocating an encode for the common case.
 * UTF-16 units are a lower bound on UTF-8 bytes, and 3× units an upper bound
 * (4-byte code points cost 2 units) — so short bodies pass and absurd ones
 * fail with plain length math, and only the band between pays for an encode.
 * A 100-message flood otherwise allocated 100 throwaway buffers. (P5.)
 */
function bodyOverBudget(body: string, encoder: TextEncoder): boolean {
  if (body.length > MAX_BODY_BYTES) return true
  if (body.length * 3 <= MAX_BODY_BYTES) return false
  return encoder.encode(body).length > MAX_BODY_BYTES
}

export function useChat(options: {
  isHost: boolean
  credential: Credential
  myName: string
}) {
  const { isHost, credential, myName } = options
  const room = useRoomContext()
  const { localParticipant } = useLocalParticipant()
  const identity = localParticipant?.identity ?? ''

  // ---- the store ----------------------------------------------------------
  const snapRef = useRef<ChatUiSnapshot>({
    messages: [],
    state: { rev: 0, freeze: false, mutes: [] },
    unread: 0,
    refusal: null,
    moderationFailed: false,
    frozen: false,
    myChatMuted: false,
    foreignSeq: 0,
    pending: 0,
    pendingFile: null,
    imageRefusal: null,
  })
  const listenersRef = useRef(new Set<() => void>())

  const identityRef = useRef(identity)
  identityRef.current = identity
  const isHostRef = useRef(isHost)
  isHostRef.current = isHost
  const credentialRef = useRef(credential)
  credentialRef.current = credential
  const myNameRef = useRef(myName)
  myNameRef.current = myName
  const panelOpenRef = useRef(false)

  const emit = useCallback((partial: Partial<ChatUiSnapshot>) => {
    const current = snapRef.current
    const next = { ...current, ...partial }
    // Derived flags ride along so consumers never recompute them.
    next.frozen = next.state.freeze
    next.myChatMuted = !isHostRef.current && next.state.mutes.includes(identityRef.current)
    snapRef.current = next
    for (const listener of listenersRef.current) listener()
  }, [])

  const subscribe = useCallback((listener: () => void) => {
    listenersRef.current.add(listener)
    return () => listenersRef.current.delete(listener)
  }, [])
  const getSnapshot = useCallback(() => snapRef.current, [])

  // Moderation truth, updated SYNCHRONOUSLY on every apply (S1) — the receive
  // gate must never be one paint behind the packet that changed the rules.
  const stateRef = useRef<ChatModerationState>(snapRef.current.state)

  const ringRef = useRef<ChatMessage[]>([])
  /**
   * False once the class has been left. Async image work outlives the hook —
   * a decode and two S3 PUTs are seconds long — and every allocation it makes
   * after teardown is permanent, because the cleanup that would have released
   * it has already run.
   */
  const aliveRef = useRef(true)
  const seenIdsRef = useRef<Set<string>>(new Set())
  /** Tombstones survive ring eviction so nothing can resurrect a delete. */
  const tombstonesRef = useRef<Set<string>>(new Set())
  /**
   * Failed-upload blobs for same-id retry (up to ~2 MB each). Declared with the
   * other stores because every row exit path — eviction, tombstone, success,
   * unmount — must release its entry, or the blob outlives the bubble on
   * exactly the device that cannot afford it. (GROK-FINAL H1.)
   */
  const retryBlobsRef = useRef(new Map<string, RetryPayload>())

  const encoder = useMemo(() => new TextEncoder(), [])
  const decoder = useMemo(() => new TextDecoder(), [])

  // ---- inbound coalescing --------------------------------------------------
  const pendingRef = useRef<ChatMessage[]>([])
  const flushScheduledRef = useRef(false)
  const flushTimerRef = useRef<number | undefined>(undefined)
  const flushRafRef = useRef<number | undefined>(undefined)
  /** Set when ring rows were edited in place (tombstone walk) outside commit. */
  const ringDirtyRef = useRef(false)

  const commit = useCallback(() => {
    flushScheduledRef.current = false
    window.clearTimeout(flushTimerRef.current)
    // The store may have been torn down (class left, tab closing). A pending
    // rAF/300ms timer, or a reconnect history fetch resolving after unmount,
    // must not mutate a dead instance's refs.
    if (!aliveRef.current) {
      pendingRef.current = []
      return
    }
    const incoming = pendingRef.current
    pendingRef.current = []

    const ring = ringRef.current
    let added = 0
    let quietAdded = 0
    for (const message of incoming) {
      if (seenIdsRef.current.has(message.id)) {
        // A duplicate can still carry news: if the id is tombstoned and the
        // in-ring row still shows a body (the delete travelled by the OTHER
        // path), upgrade the row now. Replaced, not mutated — memoized bubbles
        // compare by reference.
        if (tombstonesRef.current.has(message.id)) {
          const index = ring.findIndex((existing) => existing.id === message.id)
          if (index >= 0 && !ring[index].deleted) {
            if (ring[index].localPreview) URL.revokeObjectURL(ring[index].localPreview!)
            ring[index] = { ...ring[index], deleted: true, body: null, attachment: null, localPreview: undefined }
            retryBlobsRef.current.delete(message.id)
            ringDirtyRef.current = true
          }
        }
        continue
      }
      seenIdsRef.current.add(message.id)
      if (tombstonesRef.current.has(message.id)) {
        // Same strip as every other tombstone path: a row born under a
        // tombstone must not carry its attachment or pin a preview either.
        if (message.localPreview) URL.revokeObjectURL(message.localPreview)
        message.body = null
        message.deleted = true
        message.attachment = null
        message.localPreview = undefined
        retryBlobsRef.current.delete(message.id)
      }
      ring.push(message)
      added += 1
      if (message.mine || message.silent) quietAdded += 1
    }
    if (added === 0 && !ringDirtyRef.current) return
    ringDirtyRef.current = false

    if (added > 0) {
      ring.sort((a, b) => (a.at - b.at) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
      if (ring.length > RING) {
        const evicted = ring.splice(0, ring.length - RING)
        for (const message of evicted) {
          seenIdsRef.current.delete(message.id)
          if (message.localPreview) URL.revokeObjectURL(message.localPreview)
          retryBlobsRef.current.delete(message.id)
        }
      }
    }
    const foreign = added - quietAdded
    emit({
      messages: [...ring],
      // Own messages never count as unread — a child who sent and closed the
      // panel before the flush was being told they had something new. (S6.)
      unread: !panelOpenRef.current && foreign > 0
        ? Math.min(snapRef.current.unread + foreign, 100)
        : snapRef.current.unread,
      foreignSeq: foreign > 0 ? snapRef.current.foreignSeq + 1 : snapRef.current.foreignSeq,
    })
  }, [emit])

  const scheduleFlush = useCallback(() => {
    if (flushScheduledRef.current) return
    flushScheduledRef.current = true
    flushRafRef.current = window.requestAnimationFrame(commit)
    // Backgrounded tabs stop painting; the timer keeps history flowing anyway.
    flushTimerRef.current = window.setTimeout(commit, 300)
  }, [commit])

  const enqueue = useCallback(
    (message: ChatMessage) => {
      // Bounded: a tampered client streaming packets between two frames cannot
      // grow this without limit. Beyond a ring's worth the oldest pending are
      // dropped — they would be evicted by the ring on commit anyway.
      const pending = pendingRef.current
      if (pending.length >= RING) {
        // The dropped row may be an optimistic image awaiting its first paint.
        // Losing it without revoking pins its thumbnail for the whole session.
        const evicted = pending.shift()
        if (evicted?.localPreview) URL.revokeObjectURL(evicted.localPreview)
      }
      pending.push(message)
      scheduleFlush()
    },
    [scheduleFlush],
  )

  /** Replace a ring row in place by id and re-emit — for optimistic image state. */
  const upgradeRow = useCallback(
    (id: string, update: (row: ChatMessage) => ChatMessage) => {
      const ring = ringRef.current
      const index = ring.findIndex((m) => m.id === id)
      if (index < 0) return
      ring[index] = update(ring[index])
      emit({ messages: [...ring] })
    },
    [emit],
  )

  // ---- tombstoning ---------------------------------------------------------
  const applyTombstone = useCallback(
    (id: string) => {
      tombstonesRef.current.add(id)
      if (tombstonesRef.current.size > TOMBSTONE_CAP) {
        const oldest = tombstonesRef.current.values().next()
        if (!oldest.done) tombstonesRef.current.delete(oldest.value)
      }
      const ring = ringRef.current
      const index = ring.findIndex((message) => message.id === id)
      if (index >= 0 && !ring[index].deleted) {
        if (ring[index].localPreview) URL.revokeObjectURL(ring[index].localPreview!)
        ring[index] = { ...ring[index], deleted: true, body: null, attachment: null, localPreview: undefined }
        emit({ messages: [...ring] })
      }
      retryBlobsRef.current.delete(id)
    },
    [emit],
  )

  // ---- moderation state (S1: stateRef is the truth, updated synchronously) --
  /** Server answers REPLACE at equal rev — the server is the only clock. */
  const applyServerState = useCallback(
    (next: ChatModerationState) => {
      if (next.rev < stateRef.current.rev) return
      stateRef.current = next
      emit({ state: next })
    },
    [emit],
  )

  /** DC events merge field-wise so freeze and mute in one tick both survive. */
  const applyModerationEvent = useCallback(
    (event: { rev: number; freeze?: boolean; mute?: { identity: string; on: boolean } }) => {
      const current = stateRef.current
      if (event.rev <= current.rev) return
      const next: ChatModerationState = { ...current, rev: event.rev }
      if (event.freeze !== undefined) next.freeze = event.freeze
      const mute = event.mute
      if (mute) {
        next.mutes = mute.on
          ? [...new Set([...current.mutes, mute.identity])]
          : current.mutes.filter((muted) => muted !== mute.identity)
      }
      stateRef.current = next
      emit({ state: next })
    },
    [emit],
  )

  // ---- snapshots (late join) -------------------------------------------------
  const snapshotAssemblies = useRef(
    new Map<string, { n: number; parts: (string | undefined)[] }>(),
  )
  const snapshotAppliedRef = useRef(false)
  const rehydratedRef = useRef(false)
  const requestLogRef = useRef(new Map<string, number>())

  const applySnapshot = useCallback(
    (snapshot: ChatSnapshot) => {
      if (!aliveRef.current) return
      if (snapshot.messages.length > 0 || snapshot.state.rev > 0 || snapshot.tombstones.length > 0) {
        snapshotAppliedRef.current = true
      }
      for (const id of snapshot.tombstones) tombstonesRef.current.add(id)
      for (const wire of snapshot.messages) {
        if (wire.deleted) tombstonesRef.current.add(wire.id)
      }
      // Rows ALREADY on screen whose delete arrived via this durable path: walk
      // the ring once and strip them. Seeding the guard alone only protected
      // future packets, and left deleted text visible until eviction. (S2.)
      const ring = ringRef.current
      for (let index = 0; index < ring.length; index += 1) {
        if (!ring[index].deleted && tombstonesRef.current.has(ring[index].id)) {
          if (ring[index].localPreview) URL.revokeObjectURL(ring[index].localPreview!)
          ring[index] = { ...ring[index], deleted: true, body: null, attachment: null, localPreview: undefined }
          ringDirtyRef.current = true
        }
      }
      applyServerState(snapshot.state)
      for (const wire of snapshot.messages) {
        pendingRef.current.push({
          id: wire.id,
          identity: wire.identity,
          name: wire.name || wire.identity,
          at: wire.at,
          body: wire.deleted ? null : wire.body,
          // Images must survive a leave/rejoin or a late join exactly like text.
          attachment: wire.deleted ? null : sanitizeIncomingAttachment(wire.attachment),
          deleted: Boolean(wire.deleted),
          // History replay: present in the list, absent from the unread badge —
          // a late joiner must not arrive to "99+ new" about the past.
          silent: true,
        })
      }
      commit()
    },
    [applyServerState, commit],
  )

  const sendRef = useRef<(wire: ChatWire, to?: string[]) => Promise<void>>(() => Promise.resolve())

  const answerRequest = useCallback((from: string) => {
    if (!isHostRef.current) return
    // Not before rehydrate: an empty answer would also cancel the asker's API
    // fallback. Unanswered is strictly better — their 1.5s fallback covers it.
    if (!rehydratedRef.current) return
    const now = Date.now()
    const last = requestLogRef.current.get(from) ?? 0
    if (now - last < REQUEST_COOLDOWN_MS) return
    requestLogRef.current.set(from, now)
    if (requestLogRef.current.size > 64) {
      for (const [key, at] of requestLogRef.current) {
        if (now - at > REQUEST_COOLDOWN_MS * 4) requestLogRef.current.delete(key)
      }
    }

    const ring = ringRef.current
    const snapshot: ChatSnapshot = {
      state: stateRef.current,
      tombstones: [...tombstonesRef.current].slice(-RING),
      messages: ring.slice(-SNAPSHOT_MESSAGES).map((message) => ({
        id: message.id,
        identity: message.identity,
        name: message.name,
        at: message.at,
        body: message.deleted ? null : message.body,
        // Keys + dimensions only — the peer's client fetches the objects itself.
        attachment: message.deleted ? null : message.attachment ?? null,
        deleted: message.deleted,
      })),
    }
    const json = JSON.stringify(snapshot)
    const parts = splitByBytes(json, SNAPSHOT_CHUNK_BYTES)
    // Chunks are addressed to the asker alone, so transfers cannot mix across
    // receivers even with a shared sid — the UUID just makes the invariant
    // local instead of depending on addressing.
    const sid = crypto.randomUUID()
    parts.forEach((part, index) => {
      void sendRef.current(
        { kind: 'chat_snapshot_chunk', sid, i: index, n: parts.length, part },
        [from],
      ).catch(() => undefined)
    })
  }, [])

  const acceptSnapshotChunk = useCallback(
    (wire: Extract<ChatWire, { kind: 'chat_snapshot_chunk' }>) => {
      if (wire.n < 1 || wire.i < 0 || wire.i >= wire.n) return
      if (wire.n > 16) return
      if (typeof wire.part !== 'string' || wire.part.length > SNAPSHOT_CHUNK_BYTES * 2) return
      let entry = snapshotAssemblies.current.get(wire.sid)
      if (!entry) {
        entry = { n: wire.n, parts: Array.from({ length: wire.n }) }
        snapshotAssemblies.current.set(wire.sid, entry)
        while (snapshotAssemblies.current.size > MAX_PENDING_SNAPSHOTS) {
          const oldest = snapshotAssemblies.current.keys().next()
          if (oldest.done) break
          snapshotAssemblies.current.delete(oldest.value)
        }
      }
      entry.parts[wire.i] = wire.part
      if (entry.parts.some((part) => part === undefined)) return
      snapshotAssemblies.current.delete(wire.sid)
      try {
        applySnapshot(JSON.parse(entry.parts.join('')) as ChatSnapshot)
      } catch {
        /* corrupt snapshot — the API fallback timer covers us */
      }
    },
    [applySnapshot],
  )

  // ---- receive ---------------------------------------------------------------
  // The data-channel handler is kept STABLE on purpose: @livekit/components-react
  // memoises the room's `DataReceived` subscription on the handler identity, so
  // a fresh inline arrow on every render tears that listener off and re-adds it
  // after each commit — EventEmitter churn on every Classroom re-render (every
  // reaction, hand raise or quality tick), and a brief window in which a
  // reliable chat or moderation packet is simply not delivered to this client
  // (reliable delivery is per-receiver and is not replayed for a missing
  // listener). The body is dispatched through a ref so it always sees the latest
  // closures without ever changing the callback identity.
  const onPacketRef = useRef<(packet: ReceivedDataMessage<typeof CHAT_TOPIC>) => void>(
    () => {},
  )
  const onPacket = useCallback(
    (packet: ReceivedDataMessage<typeof CHAT_TOPIC>) => onPacketRef.current(packet),
    [],
  )
  const { send } = useDataChannel(CHAT_TOPIC, onPacket)
  onPacketRef.current = (packet) => {
    let parsed: unknown
    try {
      parsed = JSON.parse(decoder.decode(packet.payload))
    } catch {
      return
    }
    // `JSON.parse("null")` succeeds and returns null, and reading .kind off it
    // threw inside the data-channel handler — one malformed packet from any
    // participant could take the receive path down for the rest of the lesson.
    if (typeof parsed !== 'object' || parsed === null) return
    const wire = parsed as ChatWire
    const from = packet.from?.identity ?? 'unknown'
    const fromTeacher = isTeacher(from)

    switch (wire.kind) {
      case 'chat': {
        if (typeof wire.body !== 'string' || typeof wire.id !== 'string') return
        // An empty id renders fine but is undeletable — the server refuses a
        // blank message_id, so a teacher's delete would silently do nothing.
        if (wire.id.length === 0 || wire.id.length > 64) return
        if (typeof wire.at !== 'number' || !Number.isFinite(wire.at)) return
        if (!fromTeacher) {
          if (stateRef.current.freeze) return
          if (stateRef.current.mutes.includes(from)) return
        }
        const attachment = sanitizeIncomingAttachment(wire.attachment)
        // A chat packet must carry SOMETHING: text or an image. An empty one is
        // a malformed or probing packet.
        if (bodyOverBudget(wire.body, encoder)) return
        if (wire.body.length === 0 && !attachment) return
        const now = Date.now()
        const at = Math.min(Math.max(wire.at, now - CLOCK_SLACK_MS), now + CLOCK_SLACK_MS)
        enqueue({
          id: wire.id,
          identity: from,
          name: packet.from?.name || from,
          at,
          body: wire.body,
          attachment,
          deleted: false,
        })
        return
      }
      case 'chat_delete':
        if (!fromTeacher) return
        if (typeof wire.id !== 'string' || !wire.id) return
        applyTombstone(wire.id)
        if (wire.state) applyServerState(wire.state)
        else if (typeof wire.rev === 'number') applyModerationEvent({ rev: wire.rev })
        return
      case 'chat_freeze':
        if (!fromTeacher) return
        // Full state wins outright; the delta path is only for packets from a
        // client that has not rolled out yet.
        if (wire.state) applyServerState(wire.state)
        else applyModerationEvent({ rev: wire.rev, freeze: wire.on })
        return
      case 'chat_mute':
        if (!fromTeacher) return
        if (typeof wire.identity !== 'string' || !wire.identity) return
        if (wire.state) applyServerState(wire.state)
        else applyModerationEvent({ rev: wire.rev, mute: { identity: wire.identity, on: wire.on } })
        return
      case 'chat_request':
        answerRequest(from)
        return
      case 'chat_snapshot_chunk':
        if (!fromTeacher) return
        acceptSnapshotChunk(wire)
        return
      default:
        return
    }
  }

  /** Fire-and-forget publish for chat traffic — a rejection is logged nowhere
   *  because a dropped bubble must not crash a class. Moderation uses the
   *  awaited variant below instead. */
  const publish = useCallback(
    (wire: ChatWire, to?: string[]): Promise<void> => {
      try {
        return Promise.resolve(
          send(encoder.encode(JSON.stringify(wire)), {
            reliable: true,
            destinationIdentities: to,
          }),
        ).then(() => undefined)
      } catch {
        return Promise.resolve()
      }
    },
    [send, encoder],
  )
  sendRef.current = publish

  // ---- audit outbox -----------------------------------------------------------
  const outboxRef = useRef<OutboxEntry[]>([])
  const outboxTimerRef = useRef<number | undefined>(undefined)
  /** Latest-ref so flushOutbox's failure path can re-arm without a dep cycle. */
  const armOutboxRef = useRef<() => void>(() => {})
  const flushOutbox = useCallback((keepalive = false) => {
    const batch = outboxRef.current.splice(0, OUTBOX_FLUSH_COUNT)
    if (batch.length === 0) return
    persistChatMessages(credentialRef.current, batch, { keepalive }).catch(() => {
      // Unmount (or a left class) must not re-arm: the unmount cleanup already
      // ran a final flush, and re-queueing here would create a fresh interval
      // that polls every OUTBOX_FLUSH_MS for the life of the tab with a
      // credential the server will never accept — a permanent network hammer
      // and battery drain on exactly the congested device this app targets.
      if (!aliveRef.current) return
      // Bounded requeue, dropping OLDEST on overflow — the newest words are the
      // ones an investigation needs most. Re-arm: the timer may have disarmed
      // on the empty queue between splice and this failure landing.
      outboxRef.current = [...batch, ...outboxRef.current].slice(-OUTBOX_CAP)
      armOutboxRef.current()
    })
  }, [])

  // Armed only while the outbox has work — a quiet 90-minute client must not
  // wake 1,350 times to find an empty queue. The tick disarms itself when the
  // queue drains; pushes re-arm.
  const armOutboxTimer = useCallback(() => {
    if (outboxTimerRef.current !== undefined) return
    outboxTimerRef.current = window.setInterval(() => {
      if (outboxRef.current.length === 0) {
        window.clearInterval(outboxTimerRef.current)
        outboxTimerRef.current = undefined
        return
      }
      flushOutbox()
    }, OUTBOX_FLUSH_MS)
  }, [flushOutbox])
  armOutboxRef.current = armOutboxTimer

  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden') flushOutbox(true)
    }
    // pagehide as well: iOS Safari discards tabs without a reliable
    // visibilitychange, and the audit trail's worst gap is the last thing a
    // child typed. Double-flush is safe — the server upsert is idempotent.
    const onPageHide = () => flushOutbox(true)
    document.addEventListener('visibilitychange', onHide)
    window.addEventListener('pagehide', onPageHide)
    return () => {
      window.clearInterval(outboxTimerRef.current)
      outboxTimerRef.current = undefined
      document.removeEventListener('visibilitychange', onHide)
      window.removeEventListener('pagehide', onPageHide)
      flushOutbox(true)
    }
  }, [flushOutbox])

  // ---- send ---------------------------------------------------------------------
  const sendTimesRef = useRef<number[]>([])

  const sendMessage = useCallback(
    (raw: string): boolean => {
      const body = raw.trim()
      if (!body) return false
      const me = identityRef.current
      const teacher = isHostRef.current || isTeacher(me)

      if (!teacher && stateRef.current.freeze) {
        emit({ refusal: 'frozen' })
        return false
      }
      if (!teacher && stateRef.current.mutes.includes(me)) {
        emit({ refusal: 'muted' })
        return false
      }
      if (bodyOverBudget(body, encoder)) {
        emit({ refusal: 'tooLong' })
        return false
      }
      const now = Date.now()
      sendTimesRef.current = sendTimesRef.current.filter(
        (at) => now - at < SEND_INTERVAL_MS * SEND_BURST,
      )
      if (sendTimesRef.current.length >= SEND_BURST) {
        emit({ refusal: 'tooFast' })
        return false
      }
      sendTimesRef.current.push(now)
      if (snapRef.current.refusal) emit({ refusal: null })

      const id = crypto.randomUUID()
      enqueue({ id, identity: me, name: myNameRef.current || me, at: now, body, deleted: false, mine: true })
      // Rejection swallowed deliberately: a dropped bubble must not crash a
      // class, and an unhandled rejection in a 90-minute session will.
      void publish({ kind: 'chat', id, body, at: now }).catch(() => undefined)
      outboxRef.current.push({ id, body, at: now })
      if (outboxRef.current.length >= OUTBOX_FLUSH_COUNT) flushOutbox()
      if (outboxRef.current.length > 0) armOutboxTimer()
      return true
    },
    [encoder, enqueue, publish, flushOutbox, armOutboxTimer, emit],
  )

  // ---- image send ------------------------------------------------------------
  // One image at a time: an impatient re-tap of the paperclip must not run a
  // second full decode alongside the first on the device least able to afford
  // it. Failed uploads keep their processed blobs in retryBlobsRef (top of the
  // hook) for a same-id tap-to-retry without re-processing.
  const uploadInFlightRef = useRef(false)

  const doUpload = useCallback(
    async (
      id: string,
      processed: RetryPayload,
      caption: string,
    ): Promise<{ ok: boolean; error?: ImageRefusal }> => {
      upgradeRow(id, (row) => ({ ...row, uploadState: 'uploading' }))
      try {
        let attachment: ChatAttachment
        if (processed.kind === 'file') {
          // LK-009 file path: raw bytes straight to S3 — nothing about a PDF
          // touches the image pipeline (no decode, no canvas, no thumbnail).
          // One object must exist before the message is published.
          const ticket = await requestFileUpload(credentialRef.current, {
            bytes: processed.size,
            content_type: processed.content_type,
          })
          await putToS3(ticket.put_url, processed.blob, processed.content_type)
          attachment = {
            kind: 'file',
            key: ticket.key,
            filename: processed.filename,
            size: processed.size,
            content_type: processed.content_type,
          }
        } else {
          const ticket = await requestUpload(credentialRef.current, {
            bytes: processed.full.size,
            thumb_bytes: processed.thumb.size,
            width: processed.width,
            height: processed.height,
          })
          // Both objects must exist before the message is published — a peer must
          // never receive a message pointing at an object that is not there yet.
          await Promise.all([
            putToS3(ticket.put_url, processed.full),
            putToS3(ticket.thumb_put_url, processed.thumb),
          ])
          attachment = {
            key: ticket.key,
            thumb_key: ticket.thumb_key,
            w: processed.width,
            h: processed.height,
            bytes: processed.full.size,
            name: processed.name,
          }
        }
        const at = Date.now()
        retryBlobsRef.current.delete(id)
        /**
         * The two things that can have happened while those PUTs were in the
         * air, both of which make publishing wrong:
         *
         * TOMBSTONED — a teacher removed this bubble mid-upload. The local row
         * already reads "Removed", but publishing now would hand every peer a
         * live message carrying the image an adult just took away. That is the
         * one outcome moderation exists to prevent, and the queue widened the
         * window from one upload to four.
         *
         * UNMOUNTED — the class was left. Nothing may be re-pinned after the
         * teardown that was supposed to release it.
         */
        if (!aliveRef.current || tombstonesRef.current.has(id)) {
          upgradeRow(id, (row) => {
            if (row.localPreview) URL.revokeObjectURL(row.localPreview)
            return { ...row, uploadState: undefined, localPreview: undefined }
          })
          return { ok: true }
        }
        // Confirm the optimistic row: drop uploading, swap in the real key, and
        // release the full-image object URL — the list only needs the ~2 KB
        // network thumbnail, and holding the full blob for the ring's lifetime
        // was pinning up to 2 MB per image on a phone.
        upgradeRow(id, (row) => {
          if (row.localPreview) URL.revokeObjectURL(row.localPreview)
          return { ...row, at, uploadState: undefined, localPreview: undefined, attachment }
        })
        void publish({ kind: 'chat', id, body: caption, at, attachment }).catch(() => undefined)
        outboxRef.current.push({ id, body: caption, at, attachment })
        if (outboxRef.current.length >= OUTBOX_FLUSH_COUNT) flushOutbox()
        if (outboxRef.current.length > 0) armOutboxTimer()
        // Images share the text burst window. Not to refuse them — the bytes are
        // already in S3 and discarding them would be the worst of both — but so
        // that a four-photo drop cannot also be the moment someone floods the
        // room with text.
        sendTimesRef.current.push(at)
        return { ok: true }
      } catch (error) {
        // Nothing is retained for a row that no longer exists, or for a store
        // that has already been torn down: retryBlobs is up to ~2 MB of decoded
        // JPEG (or one raw file) per entry, and after unmount nothing will ever
        // clear it again.
        if (!aliveRef.current || tombstonesRef.current.has(id)) {
          upgradeRow(id, (row) => {
            if (row.localPreview) URL.revokeObjectURL(row.localPreview)
            return { ...row, uploadState: undefined, localPreview: undefined }
          })
          return { ok: false, error: 'upload' }
        }
        // Keep the processed blobs for a same-id retry; mark the row failed.
        // Capped: each abandoned failure pins up to ~2 MB of decoded JPEG.
        if (retryBlobsRef.current.size >= RETRY_BLOBS_CAP) {
          const oldest = retryBlobsRef.current.keys().next()
          if (!oldest.done) retryBlobsRef.current.delete(oldest.value)
        }
        retryBlobsRef.current.set(id, processed)
        upgradeRow(id, (row) => ({ ...row, uploadState: 'failed' }))
        const kind = error instanceof ImagePipelineError ? error.kind : 'upload'
        return { ok: false, error: kind }
      }
    },
    [upgradeRow, publish, flushOutbox, armOutboxTimer],
  )

  /** Waiting images. The head is whatever the pump is working on right now. */
  const imageQueueRef = useRef<Array<{ file: File; caption: string }>>([])
  const pumpingRef = useRef(false)

  /**
   * Image refusals expire. They are reported by a queue that runs long after the
   * gesture, so unlike the send refusals there is no next keystroke guaranteed
   * to clear them — and a stale red line under the composer reads as a chat that
   * is still broken half an hour after the one photo that failed.
   */
  const imageRefusalTimerRef = useRef<number | undefined>(undefined)
  const flagImageRefusal = useCallback(
    (refusal: ImageRefusal | null) => {
      emit({ imageRefusal: refusal })
      window.clearTimeout(imageRefusalTimerRef.current)
      if (refusal) {
        imageRefusalTimerRef.current = window.setTimeout(() => emit({ imageRefusal: null }), 6000)
      }
    },
    [emit],
  )
  useEffect(() => () => window.clearTimeout(imageRefusalTimerRef.current), [])

  /** The file currently being PUT — outranks a queued one on the composer chip. */
  const pendingFileMetaRef = useRef<{ name: string; bytes: number } | null>(null)

  /** The queue + composer counters, published on every queue transition. */
  const emitPending = useCallback(() => {
    const pending = imageQueueRef.current.length + (uploadInFlightRef.current ? 1 : 0)
    // The composer's file chip (LK-009): the in-flight file, else the first
    // queued one; nothing pending means no chip.
    const queuedFile = imageQueueRef.current.find((entry) => isSupportedChatFile(entry.file))
    const nextFile =
      pending === 0
        ? null
        : pendingFileMetaRef.current ??
          (queuedFile ? { name: queuedFile.file.name, bytes: queuedFile.file.size } : null)
    if (pending === 0) pendingFileMetaRef.current = null
    const prevFile = snapRef.current.pendingFile
    const fileChanged =
      Boolean(prevFile) !== Boolean(nextFile) ||
      (nextFile != null && prevFile != null && (nextFile.name !== prevFile.name || nextFile.bytes !== prevFile.bytes))
    if (pending !== snapRef.current.pending || fileChanged) emit({ pending, pendingFile: nextFile })
  }, [emit])

  const sendOne = useCallback(
    async (file: File, caption: string): Promise<{ ok: boolean; error?: ImageRefusal }> => {
      const me = identityRef.current
      const teacher = isHostRef.current || isTeacher(me)
      // Distinct kinds: the composer is disabled while frozen/muted, so landing
      // here means the freeze/mute raced the file picker — the child needs the
      // real reason, not "tap to try again" (which cannot succeed). (M3.)
      if (!teacher && stateRef.current.freeze) return { ok: false, error: 'frozen' }
      if (!teacher && stateRef.current.mutes.includes(me)) return { ok: false, error: 'muted' }

      const id = crypto.randomUUID()
      // LK-009 file branch: raw bytes, no canvas pipeline, no preview object
      // URL to manage — the chip renders from metadata alone.
      if (isSupportedChatFile(file)) {
        const refusal = chatFileRefusal(file)
        if (refusal) return { ok: false, error: refusal }
        if (!aliveRef.current) return { ok: false, error: 'upload' }
        // (The composer chip's meta is owned by the pump, at the shift.)
        enqueue({
          id, identity: me, name: myNameRef.current || me, at: Date.now(),
          body: caption, deleted: false, mine: true,
          uploadState: 'uploading',
          attachment: { kind: 'file', key: '', filename: sanitizeFilename(file.name), size: file.size, content_type: file.type },
        })
        return await doUpload(
          id,
          { kind: 'file', blob: file, filename: sanitizeFilename(file.name), size: file.size, content_type: file.type },
          caption,
        )
      }
      try {
        const raw = await processImage(file)
        // The decode can take seconds on the target phone, and leaving the class
        // is exactly what an impatient child does during it. Creating the object
        // URL now would create it AFTER the cleanup that revokes them, so it
        // would live until the tab closed.
        if (!aliveRef.current) return { ok: false, error: 'upload' }
        const processed = { kind: 'image' as const, full: raw.full, thumb: raw.thumb, width: raw.width, height: raw.height, name: raw.name }
        // Optimistic bubble previews from the THUMBNAIL blob, not the full image:
        // the list renders a 200 px box, so decoding the full ≤1600 px asset into
        // it wastes memory and CPU on exactly the wrong device.
        const preview = URL.createObjectURL(processed.thumb)
        enqueue({
          id, identity: me, name: myNameRef.current || me, at: Date.now(),
          body: caption, deleted: false, mine: true,
          localPreview: preview, uploadState: 'uploading',
          attachment: { key: '', w: processed.width, h: processed.height, name: processed.name },
        })
        return await doUpload(id, processed, caption)
      } catch (error) {
        const kind = error instanceof ImagePipelineError ? error.kind : 'upload'
        return { ok: false, error: kind }
      }
    },
    [enqueue, doUpload],
  )

  /**
   * Drains the queue ONE AT A TIME.
   *
   * Serial is the whole point. `processImage` is the most expensive thing this
   * app does — a full decode, a stepwise downscale and two JPEG encodes — and
   * running four of them at once is precisely the out-of-memory tab kill the
   * pipeline's OOM guards were written to prevent. So the queue does not buy
   * parallelism; it buys the tutor the right to drop three photos and look away.
   *
   * `uploadInFlightRef` remains the single mutex, and the pump is re-entrant by
   * design: every caller may invoke it, and only the one that finds the mutex
   * free does any work.
   */
  const pumpQueue = useCallback(async (): Promise<void> => {
    // Two guards, not one. `uploadInFlightRef` says an upload is happening (a
    // retry may own it); `pumpingRef` says this loop is already draining. One
    // flag doing both jobs leaves a window between releasing it and taking it
    // again for the next item, and a second pump entering there would run two
    // decodes at once — the exact out-of-memory the serial queue exists to stop.
    if (pumpingRef.current || uploadInFlightRef.current) return
    pumpingRef.current = true
    try {
      for (;;) {
        const next = imageQueueRef.current.shift()
        if (!next) break
        // The composer's file chip follows the item being worked on — set
        // HERE, at the shift, or the chip blanks for the whole upload (the
        // entry has left the queue and sendOne has not said anything yet).
        pendingFileMetaRef.current = isSupportedChatFile(next.file)
          ? { name: next.file.name, bytes: next.file.size }
          : null
        uploadInFlightRef.current = true
        emitPending()
        try {
          const result = await sendOne(next.file, next.caption)
          if (!result.ok) flagImageRefusal(result.error ?? 'upload')
        } catch {
          // sendOne resolves rather than rejects, but a queue that can be
          // stopped by one surprise is a queue that strands the mutex — and a
          // stranded mutex means no image sends again for the rest of the class.
          flagImageRefusal('upload')
        } finally {
          uploadInFlightRef.current = false
        }
        emitPending()
      }
    } finally {
      pumpingRef.current = false
      pendingFileMetaRef.current = null
      emitPending()
    }
  }, [sendOne, flagImageRefusal, emitPending])

  /**
   * Accept a drop, a paste or a pick. Returns what it took so the panel can say
   * so; everything after that is the pump's business and is reported through
   * the store.
   */
  const queueAttachments = useCallback(
    (files: File[], rawCaption = ''): { accepted: number; dropped: number; captionRejected: boolean } => {
      if (files.length === 0) return { accepted: 0, dropped: 0, captionRejected: false }
      // The composer caps CHARACTERS; the wire caps BYTES, and Arabic — the
      // default language here — costs two bytes a letter. An over-budget caption
      // is not a cosmetic problem: every receiving client drops the whole packet
      // on the same check, so the photo would appear for the sender and for
      // nobody else in the class. Send the picture, keep their words in the box.
      const captionRejected = rawCaption.length > 0 && bodyOverBudget(rawCaption, encoder)
      const caption = captionRejected ? '' : rawCaption
      // Clear on the way in as well as set. This channel is otherwise only reset
      // by a successful TEXT send, so shortening the caption and sending the
      // picture successfully still left "that message is too long" on screen,
      // describing an attempt that had already been fixed.
      if (captionRejected) emit({ refusal: 'tooLong' })
      else if (snapRef.current.refusal === 'tooLong') emit({ refusal: null })
      // The in-flight item counts. Otherwise a cap of four holds five Files
      // while telling the tutor the limit is four.
      const held = imageQueueRef.current.length + (uploadInFlightRef.current ? 1 : 0)
      const room = Math.max(0, CHAT_QUEUE_CAP - held)
      const accepted = files.slice(0, room)
      // The caption belongs to the FIRST image only. Repeating one caption
      // across four photos reads as four people saying the same thing, and
      // captioning them individually needs a UI a live lesson has no room for.
      for (let index = 0; index < accepted.length; index += 1) {
        imageQueueRef.current.push({ file: accepted[index], caption: index === 0 ? caption : '' })
      }
      if (snapRef.current.imageRefusal) flagImageRefusal(null)
      emitPending()
      void pumpQueue()
      return {
        accepted: accepted.length,
        dropped: files.length - accepted.length,
        captionRejected,
      }
    },
    [pumpQueue, flagImageRefusal, emitPending, emit, encoder],
  )

  /** The panel reports its own client-side refusals (a dropped PDF) the same way. */
  const reportImageRefusal = flagImageRefusal

  /** Tap a failed image bubble: re-upload the SAME id with the blobs already
   *  processed — no second decode, no second ring shell. */
  const retryImage = useCallback(
    async (id: string): Promise<void> => {
      const processed = retryBlobsRef.current.get(id)
      if (!processed) return
      // Tapping a failed bubble while the queue is still draining did nothing at
      // all — no upload, no word about why. The tap is not wrong, it is early.
      if (uploadInFlightRef.current || pumpingRef.current) {
        flagImageRefusal('busy')
        return
      }
      // The caption was written once and lives on the row; a retry must not
      // quietly send the photo stripped of the sentence explaining it.
      const caption = ringRef.current.find((row) => row.id === id)?.body ?? ''
      uploadInFlightRef.current = true
      emitPending()
      try {
        const result = await doUpload(id, processed, caption)
        if (!result.ok) flagImageRefusal(result.error ?? 'upload')
        else if (snapRef.current.imageRefusal) flagImageRefusal(null)
      } finally {
        uploadInFlightRef.current = false
        emitPending()
        // A retry that lands while photos are still queued must hand the mutex
        // back to the pump, not leave the rest of the drop stalled behind it.
        void pumpQueue()
      }
    },
    [doUpload, flagImageRefusal, emitPending, pumpQueue],
  )

  // Leaving the class releases everything chat pinned: failed-upload blobs and
  // any object URLs still alive on ring rows. The SPA outlives the room, so
  // without this a failed photo's 2 MB blob survives into the next screen.
  useEffect(() => {
    // The setup half exists for StrictMode, and it is not decoration. React 18
    // mounts effects setup → cleanup → setup while KEEPING refs, so a cleanup
    // that only ever clears this flag latches it false on the very first mount
    // — and then every image send in `npm run dev` fails forever, with a
    // "tap to try again" that also cannot work. Symmetric setup/cleanup is the
    // whole fix. Fast Refresh re-runs effects the same way, so the alternative
    // was a feature that broke on every hot reload.
    aliveRef.current = true
    return () => {
      // Set FIRST. Everything below releases memory, and an upload still in
      // flight would otherwise re-pin it a moment later on a store nobody will
      // ever clean again — the leak survives into the next screen.
      aliveRef.current = false
      // Cancel any pending inbound coalesce so it cannot fire commit() on a
      // dead store after the refs below are released.
      if (flushRafRef.current !== undefined) window.cancelAnimationFrame(flushRafRef.current)
      window.clearTimeout(flushTimerRef.current)
      pendingRef.current = []
      for (const row of ringRef.current) {
        if (row.localPreview) URL.revokeObjectURL(row.localPreview)
      }
      retryBlobsRef.current.clear()
      // Queued entries pin the original `File`, which on a phone is a camera
      // photo the browser has not finished with. Leaving the lesson drops them.
      imageQueueRef.current = []
    }
  }, [])

  // ---- moderation (host) -----------------------------------------------------
  const moderationFailTimerRef = useRef<number | undefined>(undefined)
  const flagModerationFailure = useCallback(() => {
    emit({ moderationFailed: true })
    window.clearTimeout(moderationFailTimerRef.current)
    moderationFailTimerRef.current = window.setTimeout(
      () => emit({ moderationFailed: false }),
      4000,
    )
  }, [emit])
  useEffect(() => () => window.clearTimeout(moderationFailTimerRef.current), [])

  const moderate = useCallback(
    async (action: ChatModerationAction, target?: { identity?: string; messageId?: string }) => {
      if (!isHostRef.current) return
      let next: ChatModerationState
      try {
        next = await moderateChat(credentialRef.current, action, {
          targetIdentity: target?.identity,
          messageId: target?.messageId,
        })
      } catch {
        // The server mints every rev — minting one locally forked the room
        // permanently. Honest failure: nothing applies, the teacher sees it.
        flagModerationFailure()
        return
      }
      applyServerState(next)
      // The DC fan-out is awaited: the server said yes, but if the ROOM never
      // heard it the teacher must not believe the class is settled. (S5.)
      try {
        if (action === 'freeze' || action === 'unfreeze') {
          await publish({ kind: 'chat_freeze', on: action === 'freeze', rev: next.rev, state: next })
        } else if ((action === 'mute' || action === 'unmute') && target?.identity) {
          await publish({
            kind: 'chat_mute', identity: target.identity, on: action === 'mute', rev: next.rev, state: next,
          })
        } else if (action === 'delete' && target?.messageId) {
          applyTombstone(target.messageId)
          await publish({ kind: 'chat_delete', id: target.messageId, rev: next.rev, state: next })
        }
      } catch {
        flagModerationFailure()
      }
    },
    [applyServerState, applyTombstone, publish, flagModerationFailure],
  )

  // ---- boot + reconnect re-sync ------------------------------------------------
  /** Resolves true only when durable state actually landed. */
  const syncFromServer = useCallback(
    (_authoritative = false): Promise<boolean> =>
      fetchChatHistory(credentialRef.current)
        .then((history) => {
          applySnapshot({
            state: history.state,
            tombstones: history.tombstones ?? [],
            messages: history.messages,
          })
          return true
        })
        .catch(() => false),
    [applySnapshot],
  )

  const requestPeerSnapshot = useCallback(() => {
    void publish({ kind: 'chat_request' }).catch(() => undefined)
    return window.setTimeout(() => {
      if (snapshotAppliedRef.current) return
      void syncFromServer()
    }, SNAPSHOT_TIMEOUT_MS)
  }, [publish, syncFromServer])

  const bootedRef = useRef(false)
  useEffect(() => {
    if (!identity || bootedRef.current) return
    bootedRef.current = true

    if (isHostRef.current) {
      // D1: the Host reads durable history FIRST and answers nobody until it
      // has — a reconnecting tutor must never become an empty authority.
      // Only a SUCCESSFUL load makes this Host an authority. Marking it in
      // `finally` meant a failed fetch still let the Host answer peers — with a
      // ring that might hold one live message, which is non-empty enough to
      // suppress the asker's own API fallback while lacking every tombstone,
      // freeze and mute the server knows about.
      void syncFromServer(true).then((ok) => {
        rehydratedRef.current = ok
      })
      return
    }

    let fallbackTimer: number | undefined
    const timer = window.setTimeout(() => {
      fallbackTimer = requestPeerSnapshot()
    }, 500)
    return () => {
      window.clearTimeout(timer)
      window.clearTimeout(fallbackTimer)
    }
  }, [identity, syncFromServer, requestPeerSnapshot])

  // Soft reconnects (the SDK recovers without unmounting anything) can miss
  // freeze/mute/delete published while the socket was down — reliable delivery
  // does not buffer for the absent. Re-sync from the durable truth. (S4.)
  useEffect(() => {
    if (!room) return
    let fallbackTimer: number | undefined
    const onReconnected = () => {
      if (isHostRef.current) {
        // Authority is surrendered UNTIL durable truth lands. The ring just
        // missed every freeze/mute/delete published while the socket was down;
        // answering peers from it would spread the gap AND cancel their own
        // API fallback (a non-empty snapshot sets snapshotApplied). On a failed
        // fetch it stays false — peers time out and read the API themselves.
        rehydratedRef.current = false
        void syncFromServer().then((ok) => {
          rehydratedRef.current = ok
        })
        return
      }
      snapshotAppliedRef.current = false
      window.clearTimeout(fallbackTimer)
      fallbackTimer = requestPeerSnapshot()
    }
    room.on(RoomEvent.Reconnected, onReconnected)
    return () => {
      room.off(RoomEvent.Reconnected, onReconnected)
      window.clearTimeout(fallbackTimer)
    }
  }, [room, syncFromServer, requestPeerSnapshot])

  // ---- panel open state (unread) ------------------------------------------------
  const setPanelOpen = useCallback(
    (open: boolean) => {
      panelOpenRef.current = open
      if (open && snapRef.current.unread > 0) emit({ unread: 0 })
    },
    [emit],
  )

  // ---- link previews -------------------------------------------------------
  // Cache + in-flight dedupe per URL, session-lifetime: RING is 200 messages,
  // a class shares a handful of links, and the server caches per URL anyway —
  // this map exists so scrolling never refetches and a null (failed) preview
  // is remembered instead of retried. No retry by policy: on a Cairo
  // connection a preview that did not come is a preview the lesson lives
  // without.
  const unfurlCacheRef = useRef(new Map<string, Promise<LinkPreview | null>>())
  const unfurl = useCallback((url: string): Promise<LinkPreview | null> => {
    const cache = unfurlCacheRef.current
    const existing = cache.get(url)
    if (existing) return existing
    const attempt = fetchUnfurl(credentialRef.current, url).catch(() => null)
    cache.set(url, attempt)
    return attempt
  }, [])

  // ---- file attachments (LK-009) --------------------------------------------
  // A private object is opened through a short-lived presigned GET the API
  // mints per message for run participants. In-flight requests dedupe (a
  // double-tap must not mint two URLs); settled entries are DROPPED rather
  // than cached — the URL is deliberately short-lived, and handing someone an
  // expired link from a session-old cache would look exactly like a broken
  // feature. A later tap simply mints a fresh one.
  const fileUrlInFlightRef = useRef(new Map<string, Promise<string | null>>())
  const fileUrl = useCallback((messageId: string): Promise<string | null> => {
    const cache = fileUrlInFlightRef.current
    const existing = cache.get(messageId)
    if (existing) return existing
    const attempt = requestFileUrl(credentialRef.current, messageId)
      .catch(() => null)
      .then((url) => {
        if (cache.get(messageId) === attempt) cache.delete(messageId)
        return url
      })
    cache.set(messageId, attempt)
    return attempt
  }, [])

  // One stable object for the whole session: consumers subscribe, Classroom
  // merely passes it down and never re-renders for chat activity.
  return useMemo(
    () => ({
      subscribe, getSnapshot, sendMessage, queueAttachments, retryImage, reportImageRefusal, fileUrl,
      moderate, setPanelOpen, unfurl,
    }),
    [
      subscribe, getSnapshot, sendMessage, queueAttachments, retryImage, reportImageRefusal, fileUrl,
      moderate, setPanelOpen, unfurl,
    ],
  )
}

export type ChatApi = ReturnType<typeof useChat>
